// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {DatePicker, TextField} from '@deere/isg.component-library';
import dayjs from 'dayjs';
import ValidationInput from 'Ui/components/common/form/validation-input';
import {onManualDataChange, validateDate} from 'Ui/components/manual-data/manual-data-utils';
import {MANUAL_DATA_TIME_FORMAT} from 'Utils/time-utils';
import {getCurrencySymbol} from 'Utils/unit-conversion-utils';
import {MIN_NUMERIC_VALUE, MAX_NUMERIC_VALUE} from 'Ui/constants/common-constants';
import {WATER_COST} from 'Common/constants/data-group-constants';
import {DOLLARS_PER_THOUSAND_GALLONS, DOLLARS_PER_THOUSAND_LITERS} from 'Common/constants/data-unit-constants';
import {UNIT_OF_MEASURE} from 'Common/constants/preference-constants';

function isUnitOfMeasureMetric(membership, values) {
    if (values?.manualData && values.manualData[0]?.data_unit) {
        return values.manualData[0]?.data_unit === DOLLARS_PER_THOUSAND_LITERS;
    }
    return membership.unitOfMeasure === UNIT_OF_MEASURE.METRIC;
}

function WaterCostEntryForm(props) {
    const {
        membership,
        setValid,
        setValues,
        translations,
        values
    } = props;

    function onValuesChange(name, value) {
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    }

    const [validatedDate, setValidatedDate] = React.useState();

    React.useEffect(() => {
        setValidatedDate(validateDate(values.timeSample, setValid));
    }, [values.timeSample]);

    const isUOMMetric = isUnitOfMeasureMetric(membership, values);
    const unit = isUOMMetric ? translations.STANDARD_UOM_LITER : translations.GALLONS;
    const dataUnit = isUOMMetric ? DOLLARS_PER_THOUSAND_LITERS : DOLLARS_PER_THOUSAND_GALLONS;

    const currencySymbol = getCurrencySymbol({
        currencyPreference: membership.currencyPreference
    });

    return (
        <div className='settings-body manual-data-form'>
            <DatePicker
                helperText={validatedDate ? '' : translations.INVALIDATED_DATE}
                label={translations.DATE}
                onChange={(date) => onValuesChange('timeSample', dayjs(date).format(MANUAL_DATA_TIME_FORMAT))}
                value={validatedDate?.toDate() || new Date()}
            />
            <ValidationInput
                debounceTimeout={window.props.debounceTimeout}
                errors={{
                    rangeUnderflow: `${translations.ONLINK_VALUE_GREATER_THAN_OR_EQUAL} ${MIN_NUMERIC_VALUE}`,
                    rangeOverflow: `${translations.ONLINK_VALUE_LESS_THAN_OR_EQUAL} ${MAX_NUMERIC_VALUE}`
                }}
                label={`${translations.ONLINK_PRICE} (${currencySymbol}/1000 ${unit})`}
                max={MAX_NUMERIC_VALUE}
                min={MIN_NUMERIC_VALUE}
                name={WATER_COST}
                onChange={(event) => onManualDataChange({
                    dataUnit,
                    name: event.target.name,
                    setValues,
                    value: event.target.value
                })}
                required={true}
                setValid={setValid}
                tabIndex={0}
                type='number'
                value={values.manualData ? values.manualData[0]?.value : ''}
            />
            <TextField
                label={translations.NOTES}
                multiline
                name='note'
                onChange={(event) => onValuesChange('note', event.target.value)}
                tabIndex={0}
                value={values.note || ''}
            />
        </div>
    );
}

WaterCostEntryForm.propTypes = {
    membership: PropTypes.membership,
    setValid: PropTypes.func,
    setValues: PropTypes.func,
    translations: PropTypes.translations,
    values: PropTypes.object
};

export default WaterCostEntryForm;
